import React, { useCallback, useEffect, useRef, useState } from "react";

import styles from "./ImageModal.module.scss";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ReactSVG } from "react-svg";
import ModalTrigger from "@/components/_primitives/Modal/ModalTrigger/ModalTrigger";
import ModalPortal from "@/components/_primitives/Modal/ModalPortal/ModalPortal";
import { ModalClose } from "@/components/_primitives/Modal/ModalClose/ModalClose";
import ModalContent from "@/components/_primitives/Modal/ModalContent/ModalContent";
import ModalOverlay from "@/components/_primitives/Modal/ModalOverlay/ModalOverlay";
import ModalRoot, {
  useModal,
} from "@/components/_primitives/Modal/ModalRoot/ModalRoot";
import { ImageType } from "@/types/baseTypes";
import {
  CarouselNextButton,
  CarouselPrevButton,
  useCarousel,
} from "@aleksundshantu.com/aunds-utils-react/primitives/carousel";
import Image from "next/image";
import { BottomNavigation } from "../BottomNavigation/BottomNavigation";
import { AnimatePresence } from "motion/react";
import * as motion from "motion/react-m";
import { ContentTag } from "@/components/ContentTag/ContentTag";

interface ImageModalProps {
  elements: {
    image: ImageType | false;
    caption: string;
  }[];
}

export const ImageModal = ({ elements }: ImageModalProps) => {
  const { getIcon, getLabel } = useGlobalContext();
  const { emblaApi } = useCarousel();
  const [selectedindex, setSelectedIndex] = useState(
    emblaApi?.selectedScrollSnap() || 0,
  );

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <ModalRoot>
      <ModalTrigger>
        <button
          aria-label={getLabel("aria_label_media_info_button")}
          className={styles["modal-btn"]}
        >
          <ReactSVG
            width={34}
            height={34}
            src={getIcon("open")}
            wrapper={"svg"}
            role="link"
            aria-hidden
            beforeInjection={(svg) => {
              svg.setAttribute("alt", `Modal Open Icon`);
              svg.setAttribute("title", `Modal Open Icon`);
              svg.setAttribute("aria-label", `Modal Open Icon`);
            }}
          />
        </button>
      </ModalTrigger>

      <ModalPortal
        portalRef={undefined} // Assuming modalInsideBody is not used here
      >
        <ModalOverlay />

        <ModalContent
          extraClassName={styles["image-modal-content"]}
          hasChildrenModal
          enableTouchSwipe={true}
        >
          <ModalClose />

          <div className={`${styles["slider-naviagtion"]} ${styles.prev}`}>
            <CarouselPrevButton />
          </div>

          <AnimatePresence mode="wait">
            {elements[selectedindex] && elements[selectedindex].image && (
              <ImageBox
                key={selectedindex}
                image={elements[selectedindex].image}
                caption={elements[selectedindex].caption}
                selectedindex={selectedindex}
                onSwipeLeft={emblaApi?.scrollNext}
                onSwipeRight={emblaApi?.scrollPrev}
              />
            )}
          </AnimatePresence>

          <div className={`${styles["slider-naviagtion"]} ${styles.next}`}>
            <CarouselNextButton />
          </div>

          <BottomNavigation
            totalSnaps={(elements || []).length}
            initialIndex={selectedindex + 1}
            hideFrom="widescreen"
          />
        </ModalContent>
      </ModalPortal>
    </ModalRoot>
  );
};

interface ImageBoxProps {
  image: ImageType;
  caption?: string;
  selectedindex: number;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

const ImageBox = ({
  image,
  caption,
  selectedindex,
  onSwipeLeft,
  onSwipeRight,
}: ImageBoxProps) => {
  const { getIcon, getLabel } = useGlobalContext();
  const [showInfo, setShowInfo] = useState(false);
  const { open, setOpen } = useModal();
  const { emblaApi } = useCarousel();
  const ImageBoxRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const handlePrevClick = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const handleNextClick = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartX.current || !touchEndX.current) return;

    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (diff > threshold && onSwipeLeft) {
      onSwipeLeft();
    } else if (diff < -threshold && onSwipeRight) {
      onSwipeRight();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  }, [onSwipeLeft, onSwipeRight]);

  useEffect(() => {
    if (!open) return; // Only enable when the main modal is visually open

    const handleKeyDown = (event: KeyboardEvent) => {
      // Prevent default scroll behavior when using arrow keys or tab inside the modal
      if (
        ["Escape", "ArrowLeft", "ArrowRight", "Tab"].includes(event.key) &&
        ImageBoxRef.current
      ) {
        event.stopPropagation();
      }

      if (event.key === "Escape") {
        if (showInfo) {
          setShowInfo(false);
        } else {
          setOpen(false);
        }
      } else if (event.key === "ArrowLeft") {
        setShowInfo(false);
        handlePrevClick();
      } else if (event.key === "ArrowRight") {
        setShowInfo(false);
        handleNextClick();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showInfo]);

  useEffect(() => {
    if (open) {
      if (!showInfo) {
        const infoButton = document.getElementById(
          `single-image-slider-info-button-${selectedindex}`,
        );
        if (infoButton) {
          setTimeout(() => infoButton.focus(), 200);
        }
      }
      if (showInfo) {
        const infoCloseButton = document.getElementById(
          `single-image-slider-close-info-button-${selectedindex}`,
        );
        if (infoCloseButton) {
          setTimeout(() => infoCloseButton.focus(), 200);
        }
      }
    }
  }, [selectedindex, open, showInfo]);
  return (
    <motion.div
      ref={ImageBoxRef}
      // variants={clip_variant}
      initial="initial"
      whileInView={"animate"}
      viewport={{ once: true }}
      exit={"exit"}
      className={styles["image-box"]}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="is-relative is-clipped w-full">
        <Image
          src={image.url}
          width={image.width} // Default width if not provided
          height={image.height} // Default height if not provided
          alt={image.alt || image.title}
          className={styles["modal-image"]}
          sizes="(max-width: 1023px) 80vw, (min-width: 1407px) 60vw, 50vw"
        />
        <button
          id={`single-image-slider-info-button-${selectedindex}`}
          tabIndex={showInfo ? -1 : 0}
          aria-label={getLabel("aria_label_media_info_button")}
          className={`${styles["media-info-btn"]} button-reset`}
          onClick={() => setShowInfo(true)}
        >
          <ReactSVG
            src={getIcon("info")}
            wrapper={"svg"}
            width={16}
            height={16}
            aria-hidden
            beforeInjection={(svg) => {
              svg.setAttribute("alt", `info Icon`);
              svg.setAttribute("aria-label", `info Icon`);
            }}
          />
        </button>
        <AnimatePresence>
          {/* {showInfo && ( */}
          <motion.div
            initial={{ height: 0, overflow: "hidden", display: "none" }}
            animate={{
              height: showInfo ? "auto" : 0,
              overflowY: showInfo ? "auto" : "hidden",
              display: showInfo ? "block" : "none",
            }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
            }}
          >
            <motion.div
              animate={{
                y: showInfo ? 0 : "100%",
                opacity: showInfo ? 1 : 0,
                padding: showInfo ? "3rem 2rem 2rem" : 0,
                overflowY: showInfo ? "auto" : "hidden",
                // scrollbarWidth: "none",
              }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
              className={styles["media-info"]}
              aria-expanded={showInfo}
            >
              <button
                tabIndex={showInfo ? 0 : -1}
                id={`single-image-slider-close-info-button-${selectedindex}`}
                aria-label={getLabel("aria_label_media_info_close_button")}
                className={`${styles["close-btn"]} button-reset`}
                onClick={() => setShowInfo(false)}
              >
                <ReactSVG
                  src={getIcon("close-circle")}
                  wrapper={"svg"}
                  width={27}
                  height={27}
                  aria-hidden
                  beforeInjection={(svg) => {
                    svg.setAttribute("alt", `info Icon`);
                    svg.setAttribute("aria-label", `info Icon`);
                  }}
                />
              </button>
              <ContentTag
                extraClass={styles["image-description"]}
                content={image.description}
              />
            </motion.div>
          </motion.div>
          {/*  )} */}
        </AnimatePresence>
      </div>

      {caption && (
        <div className={styles["image-caption"]}>
          <p
            className="small pb-0 mb-0"
            dangerouslySetInnerHTML={{ __html: caption }}
          />
        </div>
      )}
    </motion.div>
  );
};
